import { Download, Edit, Share2 } from "lucide-react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { EventTimeline } from "../../components/app/report/EventTimeline";
import { SummaryView } from "../../components/app/report/SummaryView";
import { DevicesView } from "../../components/app/report/DevicesView";
import { RawView } from "../../components/app/report/RawView";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { formatDateRange } from "../../lib/baseline/format";
import { useSession } from "../../services/SessionContext";

const tabs = ["timeline", "summary", "devices", "raw"] as const;

export function UploadDetailPage() {
  const { uploadId } = useParams();
  const { repo } = useSession();
  const [params, setParams] = useSearchParams();
  if (!uploadId) return <Navigate to="/app/dashboard" replace />;
  const upload = repo.getUpload(uploadId);
  if (!upload) return <Navigate to="/app/dashboard" replace />;
  const currentUpload = upload;
  const events = repo.getUploadEvents(currentUpload.uploadId);
  const summary = repo.getUploadSummary(currentUpload.uploadId);
  const activeTab = (params.get("tab") as (typeof tabs)[number]) || "timeline";

  function setTab(tab: (typeof tabs)[number]) {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next);
  }

  function downloadOriginal() {
    const blob = new Blob([currentUpload.originalFileContent ?? ""], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = currentUpload.originalFileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5 pb-20 md:pb-0">
      <Card className="overflow-hidden">
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-navy-950">{currentUpload.projectName || currentUpload.originalFileName}</h1>
              <p className="mt-1 text-sm text-slate-600">{currentUpload.controllerName || "Unnamed controller"}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge className="bg-sky-50 text-sky-800 ring-sky-200">{formatDateRange(currentUpload.dateRangeStart, currentUpload.dateRangeEnd)}</Badge>
                <Badge className="bg-slate-50 text-slate-700 ring-slate-200">{currentUpload.rowCount.toLocaleString()} events</Badge>
                <Badge className="bg-red-50 text-red-800 ring-red-200">{currentUpload.criticalIssueCount + currentUpload.warningCount} issues</Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary">
                <Link to={`/app/uploads/${currentUpload.uploadId}/share`}>
                  <Share2 className="h-4 w-4" aria-hidden /> Share
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to={`/app/uploads/${currentUpload.uploadId}/edit`}>
                  <Edit className="h-4 w-4" aria-hidden /> Edit Details
                </Link>
              </Button>
              <Button type="button" variant="secondary" onClick={downloadOriginal}>
                <Download className="h-4 w-4" aria-hidden /> Download Original CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2 overflow-x-auto border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`min-h-11 whitespace-nowrap border-b-2 px-4 text-sm font-semibold capitalize ${
              activeTab === tab
                ? "border-navy-900 text-navy-900"
                : "border-transparent text-slate-600 hover:text-navy-900"
            }`}
            onClick={() => setTab(tab)}
            type="button"
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "timeline" ? <EventTimeline events={events} upload={currentUpload} eventLinkBase={`/app/uploads/${currentUpload.uploadId}/events`} /> : null}
      {activeTab === "summary" ? <SummaryView summary={summary} /> : null}
      {activeTab === "devices" ? <DevicesView summary={summary} uploadId={currentUpload.uploadId} /> : null}
      {activeTab === "raw" ? <RawView events={events} /> : null}
    </div>
  );
}
